import { makeStyles, tokens, Card, Body1, Caption1 } from '@fluentui/react-components';

const useStyles = makeStyles({
  card: {
    padding: tokens.spacingVerticalL,
    minWidth: '200px',
    flex: '1 1 200px',
    borderLeft: `3px solid ${tokens.colorBrandBackground}`,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: tokens.spacingVerticalS,
  },
  icon: {
    width: '40px',
    height: '40px',
    borderRadius: tokens.borderRadiusMedium,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
  },
  value: {
    fontSize: '32px',
    fontWeight: tokens.fontWeightBold,
    lineHeight: 1.2,
    marginBottom: tokens.spacingVerticalXS,
  },
  label: {
    color: tokens.colorNeutralForeground3,
  },
  trend: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    marginTop: tokens.spacingVerticalS,
    fontSize: tokens.fontSizeBase200,
  },
  trendPositive: {
    color: tokens.colorPaletteGreenForeground1,
  },
  trendNegative: {
    color: tokens.colorPaletteRedForeground1,
  },
  trendNeutral: {
    color: tokens.colorNeutralForeground3,
  },
});

interface StatCardProps {
  title: string;
  value: number | string;
  icon?: React.ReactNode;
  iconColor?: string;
  iconBackground?: string;
  trend?: {
    value: number;
    label: string;
    direction: 'up' | 'down' | 'neutral';
  };
}

export function StatCard({
  title,
  value,
  icon,
  iconColor = tokens.colorBrandForeground1,
  iconBackground = tokens.colorBrandBackground2,
  trend,
}: StatCardProps) {
  const styles = useStyles();

  const getTrendClass = () => {
    if (!trend) return '';
    switch (trend.direction) {
      case 'up':
        return styles.trendPositive;
      case 'down':
        return styles.trendNegative;
      default:
        return styles.trendNeutral;
    }
  };

  const getTrendIcon = () => {
    if (!trend) return null;
    switch (trend.direction) {
      case 'up':
        return '↑';
      case 'down':
        return '↓';
      default:
        return '→';
    }
  };

  return (
    <Card className={styles.card}>
      <div className={styles.header}>
        <div>
          <div className={styles.value}>{value}</div>
          <Body1 className={styles.label}>{title}</Body1>
        </div>
        {icon && (
          <div
            className={styles.icon}
            style={{ backgroundColor: iconBackground, color: iconColor }}
          >
            {icon}
          </div>
        )}
      </div>
      {trend && (
        <div className={`${styles.trend} ${getTrendClass()}`}>
          <span>{getTrendIcon()}</span>
          <span>{Math.abs(trend.value)}%</span>
          <Caption1>{trend.label}</Caption1>
        </div>
      )}
    </Card>
  );
}
